import Tile from 'ol/Tile';
import ImageTile from 'ol/ImageTile';
import TileState from 'ol/TileState';
import { BaseLayerHealthSettings } from './BaseLayerHealthSettings';

export type TileFailureReason = 'noResponse' | 'networkError';

const failureListeners: Array<(tile: Tile, reason: TileFailureReason) => void> = [];

/**
 * Called when a tile fails because the server could not be reached: no response in time,
 * or a network-level error (refused, DNS, offline). HTTP error statuses such as 404 are
 * normal (edge tiles, historical coverage gaps) and are not reported here.
 */
export function addTileFailureListener(listener: (tile: Tile, reason: TileFailureReason) => void): void {
    failureListeners.push(listener);
}

const cancellers = new WeakMap<Tile, () => void>();

/**
 * Aborts a tile still being fetched by fetchTileLoadFunction and ends it in the error
 * state, freeing its tile queue slot at once. Not reported as a server failure. Does
 * nothing for a tile that is not (or is no longer) being fetched.
 */
export function cancelTileLoad(tile: Tile): void {
    const cancel = cancellers.get(tile);
    if (cancel) {
        cancel();
    }
}

/**
 * Tile load function that fetches the tile itself so a dead server can be told apart from
 * a slow connection, which an <img> cannot do:
 *  - no response headers within tileConnectTimeoutMs: server treated as dead, request aborted;
 *  - once headers arrive the body may take up to tileDownloadTimeoutMs (slow networks).
 * Ending a failed tile in the error state is what frees its slot in the OpenLayers tile
 * queue (16 map-wide) so other layers can load. Needs CORS on the tile server.
 */
export function fetchTileLoadFunction(tile: Tile, src: string): void {
    const image = (tile as ImageTile).getImage() as HTMLImageElement;
    const controller = new AbortController();
    let timer = window.setTimeout(function () {
        fail('noResponse');
    }, BaseLayerHealthSettings.tileConnectTimeoutMs);
    let finished = false;
    cancellers.set(tile, function () {
        fail(null);
    });

    function fail(reason: TileFailureReason | null): void {
        if (finished) {
            return;
        }
        finished = true;
        window.clearTimeout(timer);
        controller.abort();
        if (reason !== null) {
            console.debug(`Basemap tile failed (${reason}): ${src}`);
            failureListeners.forEach((listener) => listener(tile, reason));
        }
        tile.setState(TileState.ERROR);
    }

    fetch(src, { signal: controller.signal })
        .then(function (response) {
            if (finished) {
                return null;
            }
            //the server answered: now allow for a slow download
            window.clearTimeout(timer);
            timer = window.setTimeout(function () {
                fail(null);
            }, BaseLayerHealthSettings.tileDownloadTimeoutMs);

            if (!response.ok) {
                //e.g. 404 for a tile outside coverage: an ordinary tile error, not an outage
                fail(null);
                return null;
            }
            return response.blob();
        })
        .then(function (blob) {
            if (blob === null || finished) {
                return;
            }
            finished = true;
            window.clearTimeout(timer);

            const url = URL.createObjectURL(blob);
            const revoke = function () {
                URL.revokeObjectURL(url);
            };
            image.addEventListener('load', revoke, { once: true });
            image.addEventListener('error', revoke, { once: true });
            image.src = url;
        })
        .catch(function () {
            //aborted by fail() (already handled), or a network-level failure
            if (!finished) {
                fail('networkError');
            }
        });
}
